/**
 * PushEnableBanner — « Activer les notifications » sur le téléphone du client (Batch #14.263).
 * Le navigateur exige un geste de l'utilisateur : ce bouton demande la permission puis enregistre
 * l'appareil (FCM). Masqué si déjà activé, non supporté, ou fermé (14 jours).
 */
import { useEffect, useState } from "react";
import { Bell, BellRing, X } from "lucide-react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { getPushStatus, requestNotificationPermission } from "@/services/firebase";

const KEY = "portal_push_dismissed_at";
const FOURTEEN_DAYS = 14 * 24 * 3600 * 1000;

export default function PushEnableBanner({ clientId, primaryColor }) {
  const [state, setState] = useState("hidden"); // hidden | ask | busy | done
  const [registeredBefore, setRegisteredBefore] = useState(false);

  useEffect(() => {
    (async () => {
      const t = Number(localStorage.getItem(KEY) || 0);
      if (t && Date.now() - t < FOURTEEN_DAYS) return;
      const s = await getPushStatus();
      if (!s.supported || !s.hasConfig) return;
      if (s.permission === "granted") {
        try {
          const r = await api.get("/portal/push/status");
          if (r.data?.registered) { setRegisteredBefore(true); return; }
        } catch { /* on propose quand même */ }
      }
      if (s.permission !== "denied") setState("ask");
    })();
  }, []);

  const enable = async () => {
    setState("busy");
    const r = await requestNotificationPermission();
    if (!r?.ok) {
      toast.error(r?.reason === "permission-denied" ? "Notifications refusées dans le navigateur" : "Impossible d'activer les notifications sur cet appareil");
      setState("ask");
      return;
    }
    try {
      const res = await api.post("/push/register", { token: r.token, role: "portal", client_id: clientId });
      if (!res.data?.ok) throw new Error(res.data?.error || "register failed");
      toast.success("Notifications activées sur cet appareil");
      setState("done");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Enregistrement impossible");
      setState("ask");
    }
  };

  if (registeredBefore || state === "hidden") return null;
  if (state === "done") {
    return (
      <div className="flex items-center gap-2 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2" data-testid="portal-push-done">
        <BellRing className="w-4 h-4" /> Vous recevrez une alerte sur ce téléphone pour chaque document à signer, message ou rappel de rendez-vous.
      </div>
    );
  }
  return (
    <div className="flex items-center gap-3 bg-white border rounded-xl px-3 py-2 shadow-sm" style={{ borderColor: primaryColor }} data-testid="portal-push-banner">
      <Bell className="w-5 h-5 shrink-0" style={{ color: primaryColor }} />
      <div className="flex-1 text-xs text-gray-700">
        <b>Recevez les alertes sur votre téléphone</b> — document à signer, message de votre gestionnaire, rappel de rendez-vous, documents de paie.
      </div>
      <button onClick={enable} disabled={state === "busy"} className="px-3 py-1.5 rounded-lg text-white text-xs font-semibold disabled:opacity-60" style={{ backgroundColor: primaryColor }} data-testid="portal-push-enable">
        {state === "busy" ? "Activation…" : "Activer"}
      </button>
      <button onClick={() => { localStorage.setItem(KEY, String(Date.now())); setState("hidden"); }} className="text-gray-400 hover:text-gray-600" aria-label="Fermer" data-testid="portal-push-dismiss"><X className="w-4 h-4" /></button>
    </div>
  );
}
